/* eslint-disable max-lines */
import { useAtom, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { useWatch } from "react-hook-form";

import {
  refineArtworkOpenAtom,
  useArtworkForm,
  useClearManualEdits
} from "@design-lab/entities/artwork";
import { pinningSessionsAtom, useCancelPinningSession } from "@design-lab/entities/pinning";
import { ConfirmActionModal } from "@design-lab/features/design-artwork/ui/ConfirmActionModal";
import {
  trackDesignLabPinningInstructionsRemoved,
  trackRemoveBackgroundClicked
} from "@design-lab/shared/lib/analytics";
import { getArtworkIdBySide, getSideFromPlacement } from "@design-lab/shared/lib/artworkUtils";
import type { ArtworkForm } from "@design-lab/shared/types";
import { Button, Label, Switch, UnionIcon } from "@design-lab/shared/ui";
import { SwitchInput } from "@design-lab/shared/ui/SwitchInput";

type PendingPinningAction =
  | { type: "refineArtwork" }
  | { type: "removeBackground"; value: boolean }
  | null;

export function RemoveBackground() {
  const { form, lens } = useArtworkForm();
  const clearManualEdits = useClearManualEdits();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingPinningAction, setPendingPinningAction] = useState<PendingPinningAction>(null);
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const { mutate: cancelSession } = useCancelPinningSession();
  const openRefine = useSetAtom(refineArtworkOpenAtom);

  // Watch entire form to avoid stale values when side changes
  const artworkForm = useWatch({
    control: form.control
  }) as ArtworkForm | undefined;

  const { currentPlacement, info } = artworkForm || {};
  const side = getSideFromPlacement(currentPlacement || "front");
  const artworkId = artworkForm ? getArtworkIdBySide(artworkForm, side) : "";
  const editImageManual = info?.[side]?.editImageManual;
  const removeBackground = info?.[side]?.removeBackground ?? true;

  // Check if there's a pinning session for the current artwork
  const sessionForArtwork = artworkId ? pinningSessions[artworkId] : undefined;
  const hasActiveSession = sessionForArtwork?.state === "active";
  const hasCompletedSession = sessionForArtwork?.state === "completed";
  const hasPinningSession = hasActiveSession || hasCompletedSession;

  // Clear modal states when side changes to prevent stale modals
  useEffect(() => {
    setPendingPinningAction(null);
    setIsConfirmModalOpen(false);
  }, [side]);

  const handleEditDesignClick = () => {
    if (hasPinningSession) {
      setPendingPinningAction({ type: "refineArtwork" });
      return;
    }
    openRefine(true);
  };

  const executeRemoveBackgroundChange = (checked: boolean) => {
    if (!artworkId) return;
    clearManualEdits(side);
    trackRemoveBackgroundClicked({
      source: "canvas",
      printQuality: "PrintPerfect",
      side,
      artworkId
    });
    form.setValue(`info.${side}.removeBackground`, checked);
  };

  const handleRemoveBackgroundChange = (checked: boolean) => {
    if (!artworkId) return;

    // Show confirmation if there's a pinning session
    if (hasPinningSession) {
      form.setValue(`info.${side}.removeBackground`, !checked);
      setPendingPinningAction({ type: "removeBackground", value: checked });
      return;
    }

    // Show confirmation if there are manual edits that will be cleared
    if (editImageManual) {
      form.setValue(`info.${side}.removeBackground`, !checked);
      setIsConfirmModalOpen(true);
      return;
    }

    executeRemoveBackgroundChange(checked);
  };

  const handlePinningConfirm = () => {
    if (artworkId && sessionForArtwork) {
      trackDesignLabPinningInstructionsRemoved({
        artworkId,
        sessionId: sessionForArtwork.sessionId
      });
      cancelSession(sessionForArtwork.sessionId, {
        onSuccess: () => {
          setPinningSessions(prev => {
            const next = { ...prev };
            delete next[artworkId];
            return next;
          });
        }
      });
    }

    // Execute the pending action
    if (pendingPinningAction?.type === "refineArtwork") {
      openRefine(true);
    } else if (pendingPinningAction?.type === "removeBackground") {
      executeRemoveBackgroundChange(pendingPinningAction.value);
    }

    setPendingPinningAction(null);
  };

  return (
    <div className="flex h-10 w-full items-center gap-1 rounded-md bg-white p-1 shadow-sm">
      <Button
        data-testid="remove-background-button"
        variant="outline"
        size="lg"
        className="relative border-none bg-transparent shadow-none"
      >
        <Label
          data-testid="remove-background-label"
          htmlFor="remove-background"
          className="absolute left-0 top-0 h-full w-full cursor-pointer"
        />

        <div className="mr-2 flex items-center gap-0.5">Background</div>

        {hasPinningSession ? (
          <Switch
            key={side}
            data-testid="remove-background-switch"
            id="remove-background"
            isSelected={removeBackground}
            onChange={handleRemoveBackgroundChange}
          />
        ) : (
          <SwitchInput
            key={side}
            data-testid="remove-background-switch"
            id="remove-background"
            lens={lens.focus(`info.${side}.removeBackground`)}
            onCheckedChange={handleRemoveBackgroundChange}
          />
        )}
      </Button>
      <Button
        type="button"
        data-testid="refine-artwork-button"
        className="bg-cta-gradient hover:bg-cta-gradient-hover flex h-8 cursor-pointer items-center justify-center gap-2 rounded-md border-0 p-1.5 px-3 text-sm text-white"
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          handleEditDesignClick();
        }}
      >
        <UnionIcon className="h-4 w-4 text-white" />
        <span>Edit Design</span>
      </Button>
      <ConfirmActionModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        title={`Turn ${removeBackground ? "Off" : "On"} Background Removal?`}
        message={`${removeBackground ? "Disabling" : "Enabling"} this will undo all background edits you've made. Do you want to continue?`}
        confirmText={`Turn ${removeBackground ? "Off" : "On"}`}
        cancelText="Keep Edits"
        onConfirm={() => {
          if (!artworkId) return;

          // Toggle to the opposite of current state
          const currentValue = form.getValues(`info.${side}.removeBackground`);
          form.setValue(`info.${side}.removeBackground`, !currentValue);
          clearManualEdits(side);
          trackRemoveBackgroundClicked({
            source: "canvas",
            printQuality: "PrintPerfect",
            side,
            artworkId
          });
          setIsConfirmModalOpen(false);
        }}
      />
      <ConfirmActionModal
        isOpen={pendingPinningAction !== null}
        onClose={() => setPendingPinningAction(null)}
        title="Remove Design Request?"
        message="This change will remove your design request, are you sure you want to proceed?"
        confirmText="Yes, Remove"
        cancelText="Keep Request"
        onConfirm={handlePinningConfirm}
      />
    </div>
  );
}
